import styled, { css } from 'styled-components'
import { getAutomatedStyleProp } from '../../../providers/ComponentsThemeProvider'
import { mediaQueries } from '../../../providers/MediaQueryProvider'
import type { ProductsListItemProps } from './types'
import { Colors } from '../../../colors'

export const Container = styled.li<{ $size: NonNullable<ProductsListItemProps['size']> }>`
  color: ${getAutomatedStyleProp('colorOnBackground', 'var(--text-primary)')};
  font-size: var(--font-size-400);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-400); /* 150% */
  letter-spacing: var(--font-letter-spacing-normal);

  ${({ $size }) =>
    $size === 'tiny' &&
    css`
      font-size: var(--font-size-300);
      line-height: var(--font-line-height-300); /* 150% */
      letter-spacing: var(--font-letter-spacing-loose);
    `};

  & + & {
    margin-top: var(--space-800);
  }
`

export const Row = styled.div`
  display: flex;
  align-items: center;
`

export const Name = styled.div`
  flex: 1;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-100);
  overflow: hidden;
  flex: 1;
`

export const Header = styled.div`
  display: flex;
  flex: 1;
`

export const ImageContainer = styled.div<{ $size: NonNullable<ProductsListItemProps['size']> }>`
  margin-right: var(--space-300);
  background-color: ${Colors.GS200};
  display: flex;
  align-items: center;
  border-radius: var(--radius-brand);

  ${({ $size }) =>
    $size === 'tiny' &&
    css`
      width: 33px;
      min-width: 33px;
      min-height: 44px;
    `};

  ${({ $size }) =>
    $size === 'small' &&
    css`
      width: 56px;
      min-width: 56px;
      min-height: 74px;
      font-size: var(--font-size-800);

      ${mediaQueries.laptop} {
        margin-right: var(--space-500);
      }
    `};

  ${({ $size }) =>
    $size === 'medium' &&
    css`
      width: 66px;
      min-width: 66px;
      min-height: 88px;
      margin-right: var(--space-500);
      font-size: 36px;
    `};

  ${({ $size }) =>
    $size === 'large' &&
    css`
      width: 60px;
      min-width: 60px;
      min-height: 80px;
      font-size: 42px;

      ${mediaQueries.laptop} {
        width: 80px;
        min-width: 80px;
        min-height: 106px;
        margin-right: var(--space-400);
      }
    `};

  img,
  svg {
    width: 100%;
    border-radius: ${getAutomatedStyleProp('imageCornerRadius')};
  }
`

export const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.GS400};
`

export const Donation = styled.div`
  flex: 1;
  text-align: center;
  user-select: none;
  color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
`

export const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--space-100);
  font-size: var(--font-size-300);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-300); /* 142.857% */
  letter-spacing: var(--font-letter-spacing-loose);

  overflow: hidden;

  &:empty {
    display: none;
  }
`

export const Description = styled.div`
  color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
`

export const Details = styled.div`
  color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-secondary)')};
`

export const Downloads = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-200);

  overflow: hidden;

  /* NOTE: This is needed due to some printing issues in Chrome browser when having this style
  enabled. This element is not needed on the printed page so it doesn't break anything. Check
  SLA-156 issue to get more details. We might want to revert this change at some point. */
  @media print {
    gap: initial;
  }

  ${mediaQueries.tablet} {
    gap: var(--space-150);
  }
`

export const DownloadsItem = styled.div`
  overflow: hidden;

  ${mediaQueries.tablet} {
    display: flex;
  }
`

export const DownloadsItemName = styled.div`
  display: flex;

  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`

export const DownloadsItemNameIcon = styled.div`
  margin-right: var(--space-200);
`

export const DownloadsItemNameText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const DownloadsItemDownload = styled.div`
  margin-top: var(--space-50);

  ${mediaQueries.tablet} {
    margin-left: var(--space-200);
  }
`

export const Quantity = styled.div`
  margin-right: var(--space-300);

  ${mediaQueries.laptop} {
    margin-right: var(--space-400);
  }
`

export const Price = styled.div`
  flex: 0 0 68px;

  text-align: right;
`
